import { AlertCircle, ChevronLeft, Loader, Sparkles, Upload, Wand2, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { API_BASE } from "../../../../consts";
import authService from "../../../../services/AuthService";
import WalmartItemForm from "./WalmartItemForm";
import { COLOR_CATEGORIES } from "./WalmartItemDetails";

const api = (path, opts) => authService.authenticatedFetch(`${API_BASE}${path}`, opts);

const fileToDataUri = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

// Keepa returns dimensions as a pre-formatted string, e.g. "10.5 x 5.25 x 3 inches"
// (see get_keepa_data_for_ebay_listing: dims_string), not a structured object.
const parseDimensionsString = (dimStr) => {
    if (!dimStr) return {};
    const nums = String(dimStr).match(/[\d.]+/g);
    if (!nums || nums.length < 3) return {};
    const [length, width, height] = nums.map(Number);
    return { length, width, height };
};

// Keepa returns weight as a pre-formatted string, e.g. "1.5 lbs"
// (see get_keepa_data_for_ebay_listing: weight_string).
const parseWeightLbs = (weightStr) => {
    if (!weightStr) return null;
    const m = String(weightStr).match(/[\d.]+/);
    return m ? Number(m[0]) : null;
};

const normalizeUpcOrEan = (raw) => {
    const digitsOnly = String(raw).replace(/\D/g, "");
    const stripped = digitsOnly.replace(/^0+/, "") || "0";
    return stripped.length <= 12 ? stripped.padStart(12, "0") : stripped;
};

// Some backend-generated URLs have the "@" in an email-based S3 path
// swapped for "/", e.g. ".../listings/founder/thesuitecircle.com/uuid.png"
// instead of ".../listings/founder@thesuitecircle.com/uuid.png".
// Operates ONLY on the pathname (never the hostname, so "amazonaws.com"
// can't get swept in), and skips any segment that already contains "@" —
// which makes this safe to call more than once on the same URL, since a
// second call on an already-fixed URL is a no-op instead of adding a
// second "@".
const fixEmailPathUrl = (url) => {
    if (!url) return url;
    try {
        const u = new URL(url);
        const parts = u.pathname.split("/");
        const tldRe = /\.(com|net|org|io|co|us|biz|info|me|edu|gov|shop)$/i;
        for (let i = 1; i < parts.length - 1; i++) {
            const seg1 = parts[i];
            const seg2 = parts[i + 1];
            if (!seg1 || !seg2) continue;
            if (seg1.includes("@") || seg2.includes("@")) continue; // already merged — leave alone
            if (tldRe.test(seg2)) {
                parts[i] = `${seg1}@${seg2}`;
                parts.splice(i + 1, 1);
                break; // fix at most one pair
            }
        }
        u.pathname = parts.join("/");
        return u.toString();
    } catch {
        return url; // not a valid absolute URL — leave untouched
    }
};

/* ─── Photo Studio — used both before the form (gathering a required image)
   and from within the form (changing the image on an already-filled listing).
   One reference photo in, either "use as-is" (upload) or "generate" (prompt-
   driven variation) out, both producing a permanent public image URL. ─── */
const PhotoStudioModal = ({ images, onImagesChange, onClose }) => {
    const [prompts, setPrompts] = useState([]);
    const [activeId, setActiveId] = useState(images[0]?.id || null);
    const [prompt, setPrompt] = useState("");
    const [saveName, setSaveName] = useState("");
    const [showSave, setShowSave] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api("/api/walmart/api/photo-prompts").then((r) => r.json()).then((d) => setPrompts(d.prompts || []));
    }, []);

    const activePhoto = images.find((i) => i.id === activeId) || null;

    const addPhotosFromFiles = async (fileList) => {
        const files = Array.from(fileList);
        if (files.length === 0) return;
        setUploading(true);
        setError("");
        const hadNoImages = images.length === 0;
        const uploaded = [];
        try {
            for (const file of files) {
                const dataUri = await fileToDataUri(file);
                const resp = await api("/api/walmart/api/upload-product-image", {
                    method: "POST", headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ image_data: dataUri }),
                });
                const data = await resp.json();
                if (!resp.ok) throw new Error(data.error || "Upload failed");
                uploaded.push({ id: crypto.randomUUID(), url: fixEmailPathUrl(data.image_url), isMain: false, source: "upload" });
            }
        } catch (err) {
            setError(err.message); // any files uploaded before the failing one are still kept below
        } finally {
            if (uploaded.length > 0) {
                if (hadNoImages) uploaded[0].isMain = true; // first photo becomes main only if this was the very first batch
                onImagesChange([...images, ...uploaded]);
                setActiveId(uploaded[uploaded.length - 1].id);
            }
            setUploading(false);
        }
    };

    const generateVariation = async () => {
        if (!prompt.trim() || !activePhoto) { setError("Pick a reference photo and describe what to change first."); return; }
        setGenerating(true);
        setError("");
        try {
            let refDataUri = activePhoto.url;
            if (!refDataUri.startsWith("data:")) {
                const imgResp = await fetch(fixEmailPathUrl(activePhoto.url));
                const blob = await imgResp.blob();
                refDataUri = await new Promise((res) => {
                    const r = new FileReader();
                    r.onload = () => res(r.result);
                    r.readAsDataURL(blob);
                });
            }
            const resp = await api("/api/walmart/api/generate-product-photo", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ image_data: refDataUri, prompt: prompt.trim() }),
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.error || "Generation failed");
            // Appended as a NEW photo — the reference is never overwritten, so the
            // user can try several variations off the same original.
            const newPhoto = { id: crypto.randomUUID(), url: fixEmailPathUrl(data.image_url), isMain: false, source: "generated" };
            onImagesChange([...images, newPhoto]);
            setActiveId(newPhoto.id);
            setPrompt("");
        } catch (err) {
            setError(err.message);
        } finally {
            setGenerating(false);
        }
    };

    const setMain = (id) => onImagesChange(images.map((i) => ({ ...i, isMain: i.id === id })));
    const removePhoto = (id) => {
        const next = images.filter((i) => i.id !== id);
        if (next.length && !next.some((i) => i.isMain)) next[0].isMain = true;
        onImagesChange(next);
        if (activeId === id) setActiveId(next[0]?.id || null);
    };

    const savePrompt = async () => {
        if (!saveName.trim() || !prompt.trim()) return;
        const resp = await api("/api/walmart/api/photo-prompts", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: saveName.trim(), prompt: prompt.trim() }),
        });
        const data = await resp.json();
        setPrompts((p) => [...p, data.prompt]);
        setShowSave(false);
        setSaveName("");
    };

    const deletePrompt = async (id) => {
        await api(`/api/walmart/api/photo-prompts/${id}`, { method: "DELETE" });
        setPrompts((p) => p.filter((x) => x.id !== id));
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col">
                <div className="bg-gradient-to-r from-purple-500 to-blue-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <div>
                        <h2 className="text-white font-bold text-lg">Photo Studio</h2>
                        <p className="text-white/80 text-xs">{images.length} photo{images.length === 1 ? "" : "s"} · click one to set as main or use it as a reference</p>
                    </div>
                    <button onClick={onClose} className="text-white/70 hover:text-white"><X size={20} /></button>
                </div>

                <div className="p-5 overflow-y-auto">
                    <div className="grid grid-cols-4 gap-3 mb-5">
                        {images.map((img) => (
                            <div key={img.id} onClick={() => setActiveId(img.id)}
                                className={`relative rounded-xl overflow-hidden border-2 cursor-pointer ${activeId === img.id ? "border-purple-500" : "border-gray-200"}`}>
                                <img src={img.url} alt="" className="w-full h-24 object-cover" />
                                {img.isMain && <span className="absolute top-1 left-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[9px] font-extrabold px-2 py-0.5 rounded-full shadow-md ring-2 ring-white">★ MAIN</span>}
                                <button onClick={(e) => { e.stopPropagation(); removePhoto(img.id); }}
                                    className="absolute top-1 right-1 bg-black/50 hover:bg-red-600 text-white rounded-full p-0.5">
                                    <X size={10} />
                                </button>
                                {!img.isMain && (
                                    <button onClick={(e) => { e.stopPropagation(); setMain(img.id); }}
                                        className="absolute bottom-1 left-1 right-1 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white text-[10px] font-extrabold py-1 rounded-md shadow-lg">
                                        Set as Main
                                    </button>
                                )}
                            </div>
                        ))}
                        <label className="border-2 border-dashed border-gray-300 rounded-xl h-24 flex flex-col items-center justify-center cursor-pointer hover:border-teal-400 text-gray-400">
                            {uploading ? <Loader size={18} className="animate-spin" /> : <Upload size={18} />}
                            <span className="text-[10px] mt-1 font-semibold">Add Another Photo</span>
                            <input type="file" accept="image/*" multiple className="hidden"
                                onChange={(e) => addPhotosFromFiles(e.target.files)} />
                        </label>
                    </div>

                    {images.length === 0 && (
                        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4 text-xs text-orange-700">
                            No photos yet — add at least one. It'll be used as your listing's main image.
                        </div>
                    )}

                    <div className="border-t border-gray-100 pt-4">
                        <p className="text-xs font-semibold text-gray-600 mb-2">
                            {activePhoto ? "Generate a variation from the selected photo:" : "Select a photo above to generate a variation from it"}
                        </p>
                        {activePhoto && (
                            <div className="flex items-center gap-3 mb-3 bg-gray-50 rounded-xl p-2">
                                <img src={activePhoto.url} alt="" className="w-12 h-12 object-cover rounded-lg" />
                                <span className="text-xs text-gray-500">This is the reference photo. Describe what should change below — Generate adds a new photo and keeps this one too.</span>
                            </div>
                        )}
                        {prompts.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                                {prompts.map((p) => (
                                    <div key={p.id} className="flex items-center gap-1 bg-gray-100 rounded-full pl-2.5 pr-1 py-1">
                                        <button onClick={() => setPrompt(p.prompt)} className="text-xs text-gray-700 font-semibold">{p.name}</button>
                                        <button onClick={() => deletePrompt(p.id)} className="text-gray-300 hover:text-red-500"><X size={10} /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
                            placeholder="e.g. clean white background, studio lighting, e-commerce product photo"
                            disabled={!activePhoto}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 mb-2 disabled:bg-gray-50" rows={2} />
                        <div className="flex gap-2 mb-2">
                            <button onClick={generateVariation} disabled={!prompt.trim() || !activePhoto || generating}
                                className="flex-1 py-2 bg-purple-600 text-white rounded-lg text-xs font-bold hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-1.5">
                                {generating ? <Loader size={12} className="animate-spin" /> : <Wand2 size={12} />} Generate New Photo
                            </button>
                            <button onClick={() => setShowSave(true)} disabled={!prompt.trim()}
                                className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-xs font-bold hover:bg-gray-200 disabled:opacity-50">
                                Save Prompt
                            </button>
                        </div>
                        {showSave && (
                            <div className="flex gap-2 mb-2">
                                <input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Prompt name"
                                    className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs" autoFocus />
                                <button onClick={savePrompt} className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-bold">Save</button>
                            </div>
                        )}
                    </div>

                    {error && <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-xs">{error}</div>}
                </div>

                <div className="p-4 border-t border-gray-100 flex-shrink-0">
                    <button onClick={onClose} disabled={images.length === 0}
                        className="w-full py-2.5 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 disabled:opacity-40">
                        Done ({images.length} photo{images.length === 1 ? "" : "s"})
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────────────────────────
   Minimal local schema flattener — only used here to compute the required
   field list for the fill-all-with-ai request. The real, full-featured
   flattener (measure/unit detection, oneOf handling, section grouping) lives
   in WalmartItemForm and is what actually renders/submits the form.
   ───────────────────────────────────────────────────────────── */
function flattenSchemaForAI(rootSchema) {
    const fields = [];
    const walk = (node, path, section) => {
        if (!node?.properties) return;
        const required = new Set(node.required || []);
        (node.oneOf || []).forEach((branch) => (branch.required || []).forEach((k) => required.add(k)));
        Object.entries(node.properties).forEach(([key, schema]) => {
            if (["productIdentifiers", "sku", "country_of_origin_substantial_transformation"].includes(key)) return;
            if (path.length === 0 && key.endsWith("FeedHeader")) return;
            if (path.length === 0 && schema.type === "array" && schema.items?.properties) return walk(schema.items, [], null);
            const newPath = [...path, key];
            const isContainer = schema.properties || (schema.type === "array" && schema.items?.properties);
            const propKeys = schema.properties ? Object.keys(schema.properties) : [];
            const isMeasureUnit = propKeys.length === 2 && propKeys.includes("measure") && propKeys.includes("unit");
            if (isMeasureUnit && path.length > 0) {
                // Split into two addressable fields — this must match how WalmartItemForm
                // reads prefilledValues back out ("<path>.measure" / "<path>.unit"), not
                // "<path>" with a "measure_unit" type. Without this split the AI's answer
                // for height/width/depth/age is never actually read.
                const isReq = required.has(key);
                fields.push({ path: [...newPath, "measure"], title: `${schema.title || key} (numeric measure)`, type: "number", enumValues: null, required: isReq });
                fields.push({ path: [...newPath, "unit"], title: `${schema.title || key} (unit)`, type: "string", enumValues: schema.properties.unit.enum || null, required: isReq });
                return;
            }
            if (isContainer) return walk(schema, newPath, path.length === 0 ? key : section);
            const isArrayField = schema.type === "array";
            fields.push({
                path: newPath, title: schema.title || key, type: schema.type,
                enumValues: schema.enum || (isArrayField ? schema.items?.enum : null) || null,
                required: required.has(key),
            });
        });
    };
    walk(rootSchema, [], null);
    return fields;
}

const WalmartNewListingFlow = ({ batchId, onAddToBatch, onBack, feedType = "MP_ITEM", version = "5.0.20260501-19_21_29-api" }) => {
    // 'gather' | 'looking_up' | 'need_image' | 'determining_type' | 'confirm_type' | 'fetching_schema' | 'filling_ai' | 'form'
    const [stage, setStage] = useState("gather");
    const [error, setError] = useState("");

    const [upc, setUpc] = useState("");
    const [productName, setProductName] = useState("");
    const [description, setDescription] = useState("");

    const [keepaData, setKeepaData] = useState(null);
    // [{ id, url, isMain, source: 'keepa' | 'upload' | 'generated' }]
    const [images, setImages] = useState([]);
    const [showPhotoStudio, setShowPhotoStudio] = useState(false);

    // Editable copies of whatever Amazon/Keepa gave us for description and
    // bullets — kept SEPARATE from keepaData itself so the raw Amazon text
    // is never what actually gets submitted; only what the seller edits here
    // flows into buildKnownValuesFromKeepa.
    const [editedDescription, setEditedDescription] = useState("");
    const [editedBullets, setEditedBullets] = useState("");

    // Derived so every existing call site that wants "the main image url" still works.
    const finalImageUrl = images.find((i) => i.isMain)?.url || images[0]?.url || null;
    const setFinalImageUrl = (url) => {
        if (!url) return;
        setImages((prev) => prev.some((i) => i.url === url)
            ? prev.map((i) => ({ ...i, isMain: i.url === url }))
            : [{ id: crypto.randomUUID(), url, isMain: true, source: "upload" }, ...prev.map((i) => ({ ...i, isMain: false }))]);
    };

    const [productType, setProductType] = useState(null);
    const [productTypeCandidates, setProductTypeCandidates] = useState([]);
    const [productTypeMatched, setProductTypeMatched] = useState(true);
    const [productTypeSearch, setProductTypeSearch] = useState("");
    const [productTypeVisibleCount, setProductTypeVisibleCount] = useState(200);
    const [pendingKeepa, setPendingKeepa] = useState(null);
    const [pendingUpc, setPendingUpc] = useState(null);

    const [knownValues, setKnownValues] = useState(null);
    const [prefilledValues, setPrefilledValues] = useState({});
    const [identifier, setIdentifier] = useState({ type: "UPC", value: "" });

    const canGatherContinue = (upc.trim() || productName.trim() || description.trim());

    const handleContinueFromGather = async () => {
        setError("");
        if (upc.trim()) {
            setStage("looking_up");
            const normalized = normalizeUpcOrEan(upc);
            try {
                const resp = await api("/api/ebay/keepa-product-data", {
                    method: "POST", headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ identifier: normalized }),
                });
                const data = await resp.json();
                if (resp.ok && data && !data.error) {
                    setKeepaData(data);
                    setIdentifier({ type: normalized.length === 13 ? "GTIN" : "UPC", value: normalized });
                    if (data.image_urls && data.image_urls.length > 0) {
                        setImages(data.image_urls.map((url, i) => ({
                            id: crypto.randomUUID(), url: fixEmailPathUrl(url), isMain: i === 0, source: "keepa",
                        })));
                        await proceedToReview(data);
                        return;
                    }
                    // Keepa gave us text but no images — go get a photo first,
                    // then still route through the rewrite/review step for the text.
                    setStage("need_image");
                    return;
                }
            } catch { /* fall through to manual path below */ }
        }
        if (images.length === 0) {
            setStage("need_image");
            return;
        }
        proceedToProductType(keepaData, upc.trim() ? normalizeUpcOrEan(upc) : null);
    };

    // Called after the need_image step. If any Amazon content was pulled at
    // all, route through the auto-rewrite + review screen; otherwise skip
    // straight to product-type detection since there's nothing to review.
    const continueAfterImages = async () => {
        if (keepaData) {
            await proceedToReview(keepaData);
        } else {
            proceedToProductType(keepaData, upc.trim() ? normalizeUpcOrEan(upc) : null);
        }
    };

    const rewriteListingCopy = async (title, brand, desc, bulletsArr) => {
        try {
            const resp = await api("/api/walmart/api/rewrite-listing-copy", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ title, brand, description: desc, bullets: bulletsArr }),
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.error || "Rewrite failed");
            return { description: data.description || desc, bullets: data.bullets || bulletsArr };
        } catch {
            // Rewrite failing shouldn't block the flow — fall back to the
            // original text rather than getting the seller stuck.
            return { description: desc, bullets: bulletsArr };
        }
    };

    // Runs the auto-rewrite (if there's any Amazon text to rewrite at all),
    // then lands on the review screen — this is the one place both entry
    // points (has-images and need-image) funnel through, so the rewrite
    // always happens exactly once and the seller never has to trigger it.
    const proceedToReview = async (keepa) => {
        if (!keepa || (!keepa.description && !(keepa.bullets || []).length)) {
            setEditedDescription("");
            setEditedBullets("");
            setStage("review_keepa");
            return;
        }
        setStage("rewriting_copy");
        const rawDesc = (keepa.description || "").replace(/<[^>]+>/g, " ").trim();
        const rawBullets = keepa.bullets || [];
        const rewritten = await rewriteListingCopy(keepa.title || productName, keepa.brand || "", rawDesc, rawBullets);
        setEditedDescription(rewritten.description);
        setEditedBullets((rewritten.bullets || []).join("\n"));
        setStage("review_keepa");
    };

    const proceedToProductType = async (keepa, upcVal) => {
        setStage("determining_type");
        try {
            const resp = await api("/api/walmart/api/determine-product-type", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    context: {
                        title: keepa?.title || productName,
                        description: keepa?.description || description,
                        amazon_category: keepa?.amazon_category || "",
                        brand: keepa?.brand || "",
                    },
                }),
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.error || "Couldn't determine product type");
            setProductType(data.product_type);
            setProductTypeMatched(data.matched !== false);
            setProductTypeCandidates(data.all_product_types || data.candidates || []);
            setPendingKeepa(keepa);
            setPendingUpc(upcVal);
            setStage("confirm_type");
        } catch (err) {
            setError(err.message);
            setStage("gather");
        }
    };

    // Maps the actual /api/ebay/keepa-product-data response shape into the same
    // knownValues shape WalmartItemForm already understands. Everything mapped
    // here fills deterministically and never needs the AI at all. Note: Keepa
    // gives no recommended-age data — those two fields are left to AI-fill.
    const buildKnownValuesFromKeepa = (keepa) => {
        const dims = parseDimensionsString(keepa?.dimensions);
        const weightLbs = parseWeightLbs(keepa?.weight);
        // Use the seller's EDITED bullets, not Keepa's raw ones — editedBullets
        // is seeded from Keepa but only ever reaches here after the seller has
        // had a chance to rewrite it on the review_keepa screen.
        const bullets = editedBullets ? editedBullets.split("\n").map((b) => b.trim()).filter(Boolean) : [];
        const keyFeaturesJoined = bullets.length > 0 ? bullets.join(", ") : "";

        const mainImg = images.find((i) => i.isMain)?.url || images[0]?.url || "";
        const secondaryImgs = images.filter((i) => i.url !== mainImg).map((i) => i.url);

        return {
            asin: keepa?.asin || "",
            productName: keepa?.title || productName || "",
            brandName: keepa?.brand || "",
            brand: keepa?.brand || "",
            manufacturer: keepa?.brand || "",
            // editedDescription is what the seller actually wrote on the
            // review_keepa screen — Keepa's raw description is only a fallback
            // for the rare case this function runs before that screen (manual
            // entry with no UPC lookup at all).
            shortDescription: (editedDescription || keepa?.description || description || "").replace(/<[^>]+>/g, " ").trim(),
            siteDescription: (editedDescription || keepa?.description || description || "").replace(/<[^>]+>/g, " ").trim(),
            mainImageUrl: mainImg,
            productSecondaryImageURL: secondaryImgs,
            price: keepa?.price ? String(keepa.price) : "",
            ShippingWeight: weightLbs != null ? String(weightLbs) : "",
            // Same WFS key-name aliasing as MPWfsItemFlow — see that comment.
            eachDepth: dims.length != null ? String(dims.length) : "",
            eachHeight: dims.height != null ? String(dims.height) : "",
            eachWidth: dims.width != null ? String(dims.width) : "",
            eachWeight: weightLbs != null ? String(weightLbs) : "",
            condition: "New",

            color: keepa?.color || "",
            colorCategory: COLOR_CATEGORIES.includes(keepa?.color) ? keepa.color : "",
            material: keepa?.material || "", // already a joined string from Keepa
            pattern: keepa?.pattern || "",
            sizeDescriptor: keepa?.size || "",
            modelNumber: keepa?.model || "",

            keyFeatures: keyFeaturesJoined,
            features: keyFeaturesJoined,
            keyFeaturesList: bullets,

            count: keepa?.number_of_items ? String(keepa.number_of_items) : "",
            countPerPack: keepa?.number_of_items ? String(keepa.number_of_items) : "1",
            multipackQuantity: "1",
            productNetContentMeasure: "1",
            productNetContentUnit: "Each",

            assembledProductHeight: dims.height != null ? { measure: dims.height, unit: "in" } : undefined,
            assembledProductWidth: dims.width != null ? { measure: dims.width, unit: "in" } : undefined,
            assembledProductLength: dims.length != null ? { measure: dims.length, unit: "in" } : undefined,
            assembledProductDepth: dims.length != null ? { measure: dims.length, unit: "in" } : undefined,
            // Same reasoning as MPItemFlow/MPWfsItemFlow — reuse the one
            // known weight value instead of letting the AI invent its own.
            assembledProductWeight: weightLbs != null ? String(weightLbs) : "",

            // No recommended-age data from Keepa — left undefined so
            // "Fill Missing with AI" (now fixed, see the flattenSchemaForAI change
            // above) picks these up on the automatic first-pass fill instead.
            minimumRecommendedAge: undefined,
            maximumRecommendedAge: undefined,

            isProp65WarningRequired: "No",
            has_written_warranty: "No",
            smallPartsWarnings: "0 - No warning applicable",
            electronicsIndicator: "No",
            batteryTechnologyType: "Does Not Contain a Battery",
            chemicalAerosolPesticide: "No",
            shipsInOriginalPackaging: "No",
            MustShipAlone: "No",
        };
    };

    // Mirrors how WalmartItemForm resolves knownValues during its own prefill
    // (bare key lookup, measure/unit split by container). Used to skip
    // re-asking the AI for anything Keepa already gave us deterministically.
    const isFieldKnownFromKeepa = (field, knownValues) => {
        if (!knownValues) return false;
        const last = field.path[field.path.length - 1];
        if (last === "measure" || last === "unit") {
            const containerKey = field.path[field.path.length - 2];
            const val = knownValues[containerKey];
            return !!val && val[last] !== undefined && val[last] !== null && val[last] !== "";
        }
        const key = field.path[field.path.length - 1];
        const val = knownValues[key];
        if (val === undefined || val === null || val === "") return false;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
    };

    const handleConfirmProductType = () => {
        if (!productType) return;
        proceedToSchemaAndFill(productType, pendingKeepa, pendingUpc);
    };

    const proceedToSchemaAndFill = async (ptype, keepa, upcVal) => {
        setStage("fetching_schema");
        try {
            const resp = await api("/api/walmart/api/item-schema", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ feed_type: feedType, version: version, product_types: [ptype] }),
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.detail || data.error || "Failed to fetch schema");

            const allFields = flattenSchemaForAI(data.schema || data);
            const required = allFields.filter((f) => f.required);
            const kv = buildKnownValuesFromKeepa(keepa);

            setStage("filling_ai");

            // Retries up to 3 passes, same pattern as WalmartItemForm's own
            // "Fill Missing with AI" button — a single AI call won't always
            // return every requested field (confirmed: maximumRecommendedAge was
            // silently dropped while its identical sibling minimumRecommendedAge
            // came back fine, on a field that IS genuinely required per the real
            // schema). This is the very first screen the user sees, so it must
            // come in fully filled — re-ask only for whatever's still missing
            // until nothing changes, and never re-ask for anything Keepa already
            // gave us deterministically.
            let currentValues = {};
            // Tracks every path the AI has ever answered for, even with a blank
            // value — once it's responded once, that's treated as its real
            // answer (e.g. "" for a registration number that genuinely doesn't
            // apply), not a gap to keep re-asking about.
            const attemptedPaths = new Set();
            for (let attempt = 0; attempt < 3; attempt++) {
                const missing = required.filter((f) => {
                    const dotted = f.path.join(".");
                    const already = currentValues[dotted];
                    if (already !== undefined && already !== null && already !== "") return false;
                    if (attemptedPaths.has(dotted)) return false;
                    if (isFieldKnownFromKeepa(f, kv)) return false;
                    return true;
                });
                if (missing.length === 0) break;

                const fillResp = await api("/api/walmart/api/fill-all-with-ai", {
                    method: "POST", headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        product_context: {
                            title: keepa?.title || productName,
                            brand: keepa?.brand || "",
                            description: keepa?.description || description,
                            bullets: keepa?.bullets || [],
                            dimensions: keepa?.dimensions || "",
                            weight: keepa?.weight || "",
                            color: keepa?.color || "",
                            material: keepa?.material || "",
                            model: keepa?.model || "",
                            size: keepa?.size || "",
                            pattern: keepa?.pattern || "",
                            binding: keepa?.binding || "",
                            included_components: keepa?.included_components || "",
                            number_of_items: keepa?.number_of_items || "",
                            product_type: ptype,
                        },
                        fields: missing.map((f) => ({
                            path: f.path.join("."), title: f.title, type: f.type, enum_values: f.enumValues,
                        })),
                    }),
                });
                const fillData = await fillResp.json();
                missing.forEach((f) => attemptedPaths.add(f.path.join(".")));
                if (!fillResp.ok || !fillData.values || Object.keys(fillData.values).length === 0) break; // no progress this round — stop retrying
                currentValues = { ...currentValues, ...fillData.values };
            }

            setPrefilledValues(currentValues);
            setKnownValues(kv);
            if (upcVal) setIdentifier({ type: identifier.type, value: upcVal });
            setStage("form");
        } catch (err) {
            setError(err.message);
            setStage("gather");
        }
    };

    if (stage === "gather") {
        return (
            <div className="flex-1 min-w-0 max-w-2xl">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                    <p className="text-sm text-gray-500 mb-5">Give us a UPC, product name, or description — whatever you have. A photo is required unless we find one automatically from your UPC.</p>

                    <label className="text-xs font-semibold text-gray-600 mb-1 block">UPC (optional)</label>
                    <input value={upc} onChange={(e) => setUpc(e.target.value)} placeholder="12-digit UPC"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono mb-4 focus:outline-none focus:border-teal-500" />

                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Product Name (optional)</label>
                    <input value={productName} onChange={(e) => setProductName(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:border-teal-500" />

                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Description (optional)</label>
                    <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-5 focus:outline-none focus:border-teal-500" />

                    {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">{error}</div>}

                    <button onClick={handleContinueFromGather} disabled={!canGatherContinue}
                        className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition disabled:opacity-40">
                        Continue
                    </button>
                </div>
            </div>
        );
    }

    if (stage === "need_image") {
        return (
            <div className="flex-1 min-w-0 max-w-2xl">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4 text-xs text-orange-700 flex items-start gap-2">
                        <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                        We couldn't find a product photo automatically. At least one photo is required — add it below.
                    </div>

                    {images.length > 0 && (
                        <div className="grid grid-cols-4 gap-2 mb-4">
                            {images.map((img) => (
                                <div key={img.id} className="relative rounded-lg overflow-hidden border border-gray-200">
                                    <img src={img.url} alt="" className="w-full h-20 object-cover" />
                                    {img.isMain && <span className="absolute top-1 left-1 bg-teal-600 text-white text-[8px] font-bold px-1 py-0.5 rounded">MAIN</span>}
                                </div>
                            ))}
                        </div>
                    )}

                    <button onClick={() => setShowPhotoStudio(true)} type="button"
                        className="w-full mb-4 py-4 border-2 border-dashed border-purple-300 bg-purple-50 rounded-xl text-purple-700 font-bold text-sm flex items-center justify-center gap-2 hover:bg-purple-100 transition">
                        <Wand2 size={16} /> {images.length > 0 ? "Add / Edit Photos in Photo Studio" : "Open Photo Studio — Add a Photo (required)"}
                    </button>

                    {showPhotoStudio && (
                        <PhotoStudioModal images={images} onImagesChange={setImages} onClose={() => setShowPhotoStudio(false)} />
                    )}

                    {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">{error}</div>}

                    <button
                        onClick={continueAfterImages}
                        disabled={images.length === 0}
                        className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition disabled:opacity-40"
                    >
                        Continue with {images.length} Photo{images.length === 1 ? "" : "s"}
                    </button>
                </div>
            </div>
        );
    }

    if (stage === "review_keepa") {
        const keepaImageCount = images.filter((i) => i.source === "keepa").length;
        return (
            <div className="flex-1 min-w-0 max-w-2xl">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                    {keepaImageCount > 0 && (
                        <div className="mb-5 bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-start gap-2.5">
                            <Sparkles size={15} className="text-orange-500 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-orange-700 leading-relaxed">
                                A couple of these photos are still the original Amazon images — worth running them through{" "}
                                <button onClick={() => setShowPhotoStudio(true)} type="button" className="font-bold underline decoration-dotted">
                                    Photo Studio
                                </button>{" "}
                                for your own version before this goes live, to avoid IP issues.
                            </p>
                        </div>
                    )}

                    {keepaImageCount > 0 && (
                        <div className="mb-5">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-xs font-bold text-gray-500 uppercase">Photos</h3>
                                <button onClick={() => setShowPhotoStudio(true)} type="button"
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-xs font-bold hover:bg-purple-100 transition">
                                    <Wand2 size={13} /> Open Photo Studio
                                </button>
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                                {images.map((img) => (
                                    <div key={img.id} className="relative rounded-lg overflow-hidden border border-gray-200">
                                        <img src={img.url} alt="" className="w-full h-20 object-cover" />
                                        {img.source === "keepa" && (
                                            <span className="absolute top-1 left-1 bg-gray-700/80 text-white text-[8px] font-semibold px-1.5 py-0.5 rounded-full">
                                                Original
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mb-4">
                        <div className="flex items-center justify-between mb-1">
                            <label className="text-xs font-semibold text-gray-600">Description</label>
                            <span className="text-[10px] text-gray-400 flex items-center gap-1"><Sparkles size={11} className="text-purple-400" /> Rewritten for you — feel free to edit</span>
                        </div>
                        <textarea value={editedDescription} onChange={(e) => setEditedDescription(e.target.value)} rows={4}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
                    </div>

                    {editedBullets && (
                        <div className="mb-5">
                            <label className="text-xs font-semibold text-gray-600 mb-1 block">Key Features / Bullets</label>
                            <textarea value={editedBullets} onChange={(e) => setEditedBullets(e.target.value)} rows={4}
                                placeholder="One bullet per line"
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500" />
                        </div>
                    )}

                    {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">{error}</div>}

                    <button
                        onClick={() => proceedToProductType(keepaData, upc.trim() ? normalizeUpcOrEan(upc) : null)}
                        className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition"
                    >
                        Continue
                    </button>
                </div>

                {showPhotoStudio && (
                    <PhotoStudioModal images={images} onImagesChange={setImages} onClose={() => setShowPhotoStudio(false)} />
                )}
            </div>
        );
    }

    if (["looking_up", "rewriting_copy", "determining_type", "fetching_schema", "filling_ai"].includes(stage)) {
        const labels = {
            looking_up: "Looking up product on Amazon...",
            rewriting_copy: "Rewriting the description in your own words...",
            determining_type: "Determining Walmart product category...",
            fetching_schema: "Fetching required fields...",
            filling_ai: "Filling in listing details with AI...",
        };
        return (
            <div className="flex-1 min-w-0 max-w-2xl">
                <div className="bg-white rounded-2xl shadow-lg p-10 flex flex-col items-center text-gray-500 text-sm">
                    <Loader size={24} className="animate-spin mb-3 text-teal-500" /> {labels[stage]}
                </div>
            </div>
        );
    }

    if (stage === "confirm_type") {
        const filtered = productTypeSearch.trim()
            ? productTypeCandidates.filter((pt) =>
                pt.toLowerCase().includes(productTypeSearch.trim().toLowerCase()))
            : productTypeCandidates;
        const visible = filtered.slice(0, productTypeVisibleCount);
        const hasMore = filtered.length > productTypeVisibleCount;

        return (
            <div className="flex-1 min-w-0 max-w-2xl">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles size={16} className="text-purple-500" />
                        <h2 className="font-bold text-gray-800">
                            {productTypeMatched ? "We think this is..." : "Pick a product type"}
                        </h2>
                    </div>

                    {productTypeMatched ? (
                        <p className="text-sm text-gray-500 mb-4">
                            Based on the product info, we picked <span className="font-semibold text-gray-800">"{productType}"</span> as the Walmart category. Search below to change it.
                        </p>
                    ) : (
                        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4 text-xs text-orange-700 flex items-start gap-2">
                            <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                            We couldn't confidently auto-detect a product type. Search the full list below and pick one to continue.
                        </div>
                    )}

                    <label className="text-xs font-semibold text-gray-600 mb-1 block">Product Type</label>
                    <input
                        value={productTypeSearch}
                        onChange={(e) => { setProductTypeSearch(e.target.value); setProductTypeVisibleCount(200); }}
                        placeholder="Search all product types..."
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:border-teal-500"
                    />
                    <div className="border border-gray-200 rounded-lg max-h-56 overflow-y-auto mb-5">
                        {visible.map((pt) => (
                            <button
                                key={pt}
                                type="button"
                                onClick={() => { setProductType(pt); setProductTypeSearch(""); setProductTypeVisibleCount(200); }}
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-teal-50 ${pt === productType ? "bg-teal-50 font-semibold text-teal-700" : "text-gray-700"}`}
                            >
                                {pt}
                            </button>
                        ))}
                        {filtered.length === 0 && (
                            <div className="px-3 py-2 text-sm text-gray-400">No matches — try a different search term.</div>
                        )}
                        {hasMore && (
                            <button
                                type="button"
                                onClick={() => setProductTypeVisibleCount((c) => c + 200)}
                                className="w-full text-center px-3 py-2 text-xs font-bold text-teal-600 hover:bg-teal-50 border-t border-gray-100"
                            >
                                Load more ({filtered.length - productTypeVisibleCount} remaining)
                            </button>
                        )}
                    </div>

                    {productType && (
                        <div className="mb-4 text-xs text-gray-500">
                            Selected: <span className="font-semibold text-gray-800">{productType}</span>
                        </div>
                    )}

                    {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">{error}</div>}

                    <button
                        onClick={handleConfirmProductType}
                        disabled={!productType}
                        className="w-full py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold hover:opacity-90 transition disabled:opacity-40"
                    >
                        {productType ? `Continue with "${productType}"` : "Select a product type to continue"}
                    </button>
                </div>
            </div>
        );
    }

    if (stage === "form") {
        const unmodifiedKeepaCount = images.filter((i) => i.source === "keepa").length;
        return (
            <>
                {unmodifiedKeepaCount > 0 && (
                    <div className="max-w-2xl mb-3 bg-orange-50 border border-orange-200 rounded-xl px-3 py-2 text-xs text-orange-700 flex items-center gap-2">
                        <Sparkles size={12} className="text-orange-500 flex-shrink-0" />
                        {unmodifiedKeepaCount} photo{unmodifiedKeepaCount === 1 ? "" : "s"} still the original Amazon image{unmodifiedKeepaCount === 1 ? "" : "s"} — run through Photo Studio for your own version before this goes live, to avoid IP issues.
                    </div>
                )}
                <WalmartItemForm
                    productType={productType}
                    knownValues={knownValues}
                    prefilledValues={prefilledValues}
                    identifierType={identifier.type}
                    identifierValue={identifier.value}
                    imageUrl={finalImageUrl}
                    secondaryImageUrls={images.filter((i) => i.url !== finalImageUrl).map((i) => i.url)}
                    itemId={null}
                    batchId={batchId}
                    feedType={feedType}
                    version={version}
                    productImages={images.map((i) => i.url)}
                    onAddToBatch={onAddToBatch}
                    onBack={() => setStage("confirm_type")}
                    titleEditable
                    imageEditable
                    onImageChange={() => setShowPhotoStudio(true)}
                />
                {showPhotoStudio && (
                    <PhotoStudioModal images={images} onImagesChange={setImages} onClose={() => setShowPhotoStudio(false)} />
                )}
            </>
        );
    }

    return null;
};

export default WalmartNewListingFlow;